/**
 * IGC（FAI 飛行記録フォーマット）の解析。
 *
 * BLS（Balloon Live Sensor / Flytec Balloon）をはじめ、FAI 公認の飛行記録計は
 * この形式でログを出す。NMEA より情報が多く、次世代 GV にとって重要な入力となる。
 *
 *   - **気圧高度と GPS 高度の両方**を持つ（競技で採点されるのは気圧高度）
 *   - **昇降率（VAR）** … 開発メモの「上昇・下降率」に直接対応
 *   - **測位精度（FXA）・衛星数（SIU）** … 風推定の品質重みに使える
 *   - 記録計・パイロット・機体が H レコードで明示される
 *
 * B レコードの拡張フィールド位置は機種ごとに異なり、**I レコードで宣言される**。
 * 位置を決め打ちにすると他機種で壊れるため、必ず I レコードから読む。
 */

import type { FixSample, SourceKind } from './types.ts';

/** B レコードの拡張フィールド定義（I レコード由来） */
export interface IgcExtension {
  /** 3文字のコード。FXA / SIU / VAR など */
  code: string;
  /** 開始バイト位置（1始まり。IGC の流儀に合わせる） */
  start: number;
  /** 終了バイト位置（1始まり・両端含む） */
  end: number;
}

export interface IgcHeader {
  /** 製造者コード3文字。例: 'XFY' */
  manufacturer: string;
  /** 記録計の識別子3文字。ファイル名にも埋め込まれる（`67VX09W2.igc` の `09W`） */
  serial: string;
  /** A レコードの付加情報。BLS では機器ID が入る（例: 'BLS00346'） */
  deviceId?: string;
  /** 記録日（UTC）。B レコードの時刻と組み合わせて絶対時刻になる */
  date: { year: number; month: number; day: number } | null;
  pilot?: string;
  crew?: string;
  gliderType?: string;
  gliderId?: string;
  /** 記録計の種別。例: 'Flytec Balloon,Balloon Live Sensor' */
  frType?: string;
  gps?: string;
  pressureSensor?: string;
  /** GPS 高度の基準。'GEO'（幾何高度）など */
  gpsAltRef?: string;
  /** 気圧高度の基準。'ISA' など */
  pressureAltRef?: string;
  /** 気圧高度の基準気圧 [hPa] */
  qnh?: number;
  datum?: string;
  /** 解釈しなかった H レコード（捨てない） */
  rawHeaders: string[];
}

export interface IgcFix {
  /** UTC epoch ミリ秒 */
  t: number;
  /** その日の UTC 秒（日付が無いログでも使える） */
  utcSeconds: number;
  lat: number;
  lon: number;
  /** 'A' = 3D 測位有効, 'V' = 無効 */
  valid: boolean;
  /** 気圧高度 [m]。競技で採点されるのはこちら */
  pressureAlt: number | null;
  /** GPS 高度 [m] */
  gpsAlt: number | null;
  /** 測位精度（FXA） */
  fixAccuracy: number | null;
  /** 使用衛星数（SIU） */
  satellites: number | null;
  /** 昇降率 [m/s]（VAR。生値は 0.1m/s 単位） */
  verticalSpeed: number | null;
}

export interface IgcFile {
  header: IgcHeader;
  extensions: IgcExtension[];
  fixes: IgcFix[];
  /** 解釈しなかった行。捨てずに保持する */
  ignored: string[];
}

/** VAR（昇降率）の生値の単位。BLS の実ログで 0.1 m/s と確認した */
const VARIO_UNIT_MPS = 0.1;

/** `DDMMYY` → 年月日。IGC の2桁年は 80 未満を 2000 年代とみなすのが慣例 */
function parseIgcDate(v: string): { year: number; month: number; day: number } | null {
  if (!/^\d{6}$/.test(v)) return null;
  const day = Number(v.slice(0, 2));
  const month = Number(v.slice(2, 4));
  const yy = Number(v.slice(4, 6));
  return { year: yy < 80 ? 2000 + yy : 1900 + yy, month, day };
}

/** IGC の緯度 `DDMMmmmN` → 度 */
function parseIgcLat(v: string): number | null {
  if (!/^\d{7}[NS]$/.test(v)) return null;
  const d = Number(v.slice(0, 2));
  const m = Number(v.slice(2, 7)) / 1000;
  const deg = d + m / 60;
  return v[7] === 'S' ? -deg : deg;
}

/** IGC の経度 `DDDMMmmmE` → 度 */
function parseIgcLon(v: string): number | null {
  if (!/^\d{8}[EW]$/.test(v)) return null;
  const d = Number(v.slice(0, 3));
  const m = Number(v.slice(3, 8)) / 1000;
  const deg = d + m / 60;
  return v[8] === 'W' ? -deg : deg;
}

/** 符号付き数値としてフィールドを読む（VAR は `-001` のような形になる） */
function signedInt(v: string): number | null {
  const t = v.trim();
  if (!/^[-+]?\d+$/.test(t)) return null;
  return Number(t);
}

/**
 * IGC ファイルを解析する。
 *
 * @param text ファイル全体
 */
export function parseIgc(text: string): IgcFile {
  const header: IgcHeader = { manufacturer: '', serial: '', date: null, rawHeaders: [] };
  const extensions: IgcExtension[] = [];
  const fixes: IgcFix[] = [];
  const ignored: string[] = [];

  let dayStartMs: number | null = null;
  /** 日またぎ検出用。UTC 秒が大きく戻ったら翌日とみなす */
  let dayOffset = 0;
  let lastUtcSeconds = -1;

  for (const raw of text.split('\n')) {
    const line = raw.replace(/[\r\n]+$/, '');
    if (line.length === 0) continue;

    switch (line[0]) {
      case 'A': {
        // A レコード: `A` + 製造者3文字 + 記録計ID3文字 + 付加情報
        // 例: `AXFY09W,BLS00346` → XFY / 09W / BLS00346
        header.manufacturer = line.slice(1, 4);
        header.serial = line.slice(4, 7).trim();
        const extra = line.slice(7).replace(/^[,\s]+/, '').trim();
        if (extra) header.deviceId = extra;
        break;
      }

      case 'H': {
        header.rawHeaders.push(line);
        const body = line.slice(1);
        // HFDTE310726 / HFDTEDATE:310726,01 の両形式に対応する
        const dte = /^.DTE(?:DATE:)?(\d{6})/.exec(body);
        if (dte) {
          header.date = parseIgcDate(dte[1]);
          if (header.date) {
            dayStartMs = Date.UTC(header.date.year, header.date.month - 1, header.date.day);
          }
          break;
        }
        const kv = /^.[A-Z0-9]{3}[^:]*:(.*)$/.exec(body);
        const value = kv ? kv[1].trim() : '';
        if (/^.PLT/.test(body)) header.pilot = value;
        else if (/^.CM2/.test(body)) header.crew = value;
        else if (/^.GTY/.test(body)) header.gliderType = value;
        else if (/^.GID/.test(body)) header.gliderId = value;
        else if (/^.FTY/.test(body)) header.frType = value;
        else if (/^.GPS/.test(body)) header.gps = value;
        else if (/^.PRS/.test(body)) header.pressureSensor = value;
        else if (/^.ALG/.test(body)) header.gpsAltRef = value;
        else if (/^.ALP/.test(body)) header.pressureAltRef = value;
        else if (/^.DTM/.test(body)) header.datum = value;
        else if (/^.ATS/.test(body)) {
          const n = Number(body.slice(4));
          if (Number.isFinite(n)) header.qnh = n;
        }
        break;
      }

      case 'I': {
        // I レコード: `I` + 拡張数2桁 + (開始2桁 終了2桁 コード3桁) の繰り返し
        const count = Number(line.slice(1, 3));
        if (!Number.isFinite(count)) break;
        for (let i = 0; i < count; i++) {
          const at = 3 + i * 7;
          const start = Number(line.slice(at, at + 2));
          const end = Number(line.slice(at + 2, at + 4));
          const code = line.slice(at + 4, at + 7);
          if (Number.isFinite(start) && Number.isFinite(end) && code.length === 3) {
            extensions.push({ code, start, end });
          }
        }
        break;
      }

      case 'B': {
        // B HHMMSS DDMMmmmN DDDMMmmmE A PPPPP GGGGG [拡張...]
        if (line.length < 35) {
          ignored.push(line);
          break;
        }
        const hh = Number(line.slice(1, 3));
        const mm = Number(line.slice(3, 5));
        const ss = Number(line.slice(5, 7));
        const lat = parseIgcLat(line.slice(7, 15));
        const lon = parseIgcLon(line.slice(15, 24));
        if (!Number.isFinite(hh) || lat === null || lon === null) {
          ignored.push(line);
          break;
        }
        const utcSeconds = hh * 3600 + mm * 60 + ss;

        // 日またぎ: 時刻が大きく巻き戻ったら翌日
        if (lastUtcSeconds >= 0 && utcSeconds + 43200 < lastUtcSeconds) dayOffset += 86400000;
        lastUtcSeconds = utcSeconds;

        const field = (code: string): string | null => {
          const ext = extensions.find((e) => e.code === code);
          if (!ext) return null;
          const v = line.slice(ext.start - 1, ext.end);
          return v.length > 0 ? v : null;
        };
        const varRaw = field('VAR');
        const fxa = field('FXA');
        const siu = field('SIU');

        fixes.push({
          t: (dayStartMs ?? 0) + dayOffset + utcSeconds * 1000,
          utcSeconds,
          lat,
          lon,
          valid: line[24] === 'A',
          pressureAlt: signedInt(line.slice(25, 30)),
          gpsAlt: signedInt(line.slice(30, 35)),
          fixAccuracy: fxa ? signedInt(fxa) : null,
          satellites: siu ? signedInt(siu) : null,
          verticalSpeed: varRaw ? (signedInt(varRaw) ?? 0) * VARIO_UNIT_MPS : null,
        });
        break;
      }

      default:
        // C（宣言）, E（イベント）, F（衛星）, G（署名）, J/K（追加データ）, L（コメント）
        ignored.push(line);
    }
  }

  return { header, extensions, fixes, ignored };
}

export interface IgcToFixOptions {
  /** 端末ID。省略時は機体ID→シリアルの順に使う */
  terminalId?: string;
  source?: SourceKind;
  /**
   * `altMsl` に何を入れるか。
   * 競技の採点は気圧高度だが、風の解析には幾何高度のほうが素直。既定は 'gps'。
   */
  altitude?: 'gps' | 'pressure';
  /** 速度・方位・昇降率を前後の測位から補完する */
  deriveMotion?: boolean;
}

/**
 * IGC を共通データモデルへ変換する。
 *
 * IGC の B レコードは速度・方位を持たないため、`deriveMotion` で前後の点から算出する。
 */
export function igcToFixSamples(igc: IgcFile, options: IgcToFixOptions = {}): FixSample[] {
  // 機体IDが未設定（BLS の既定は 'NKN'）なら機器IDへ、それも無ければ記録計IDへ落とす
  const terminalId =
    options.terminalId ??
    (igc.header.gliderId && igc.header.gliderId !== 'NKN'
      ? igc.header.gliderId
      : (igc.header.deviceId ?? igc.header.serial ?? 'IGC'));
  const source = options.source ?? 'balloon';
  const useGps = (options.altitude ?? 'gps') === 'gps';

  const out: FixSample[] = igc.fixes
    .filter((f) => f.valid)
    .map((f) => ({
      terminalId,
      t: f.t,
      timeQuality: (igc.header.date ? 'measured' : 'derived') as FixSample['timeQuality'],
      lat: f.lat,
      lon: f.lon,
      altMsl: useGps ? f.gpsAlt : f.pressureAlt,
      altPressure: f.pressureAlt,
      speed: null,
      heading: null,
      verticalSpeed: f.verticalSpeed,
      accuracy: f.fixAccuracy,
      altAccuracy: null,
      source,
    }));

  if (options.deriveMotion !== false) deriveMotion(out);
  return out;
}

function pad(n: number, width: number): string {
  return String(Math.trunc(n)).padStart(width, '0');
}

/** 度 → IGC の `DDMMmmmN/S` */
function formatIgcLat(deg: number): string {
  const hemi = deg < 0 ? 'S' : 'N';
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const mThou = Math.round((abs - d) * 60 * 1000);
  return `${pad(d, 2)}${pad(Math.floor(mThou / 1000), 2)}${pad(mThou % 1000, 3)}${hemi}`;
}

/** 度 → IGC の `DDDMMmmmE/W` */
function formatIgcLon(deg: number): string {
  const hemi = deg < 0 ? 'W' : 'E';
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const mThou = Math.round((abs - d) * 60 * 1000);
  return `${pad(d, 3)}${pad(Math.floor(mThou / 1000), 2)}${pad(mThou % 1000, 3)}${hemi}`;
}

/** 気圧高度・GPS高度 [m] → IGC の5桁固定幅（負値は先頭1桁が符号） */
function formatIgcAlt(v: number | null): string {
  const n = Math.round(v ?? 0);
  return n < 0 ? `-${pad(-n, 4)}` : pad(n, 5);
}

/** VAR拡張フィールド（昇降率）→ 符号+3桁（0.1m/s単位） */
function formatIgcVar(v: number | null): string {
  const tenths = Math.round((v ?? 0) / VARIO_UNIT_MPS);
  return `${tenths < 0 ? '-' : '+'}${pad(Math.abs(tenths), 3)}`;
}

function formatIgcTime(t: number): string {
  const d = new Date(t);
  return `${pad(d.getUTCHours(), 2)}${pad(d.getUTCMinutes(), 2)}${pad(d.getUTCSeconds(), 2)}`;
}

export interface BuildIgcOptions {
  /** ヘッダに書く記録日（UTC）。省略時は最初の実測点の UTC 日付 */
  date?: { year: number; month: number; day: number };
}

/**
 * `FixSample[]` から IGC テキストを組み立てる。
 *
 * ライブ受信中の他機データは共有サーバー側で日本時間12時にリセットされ、
 * 消えると取り戻せない。消える前にローカルへ保存し、あとで `parseIgc` で
 * 読み直して調べられるようにするための書き出し。FAI 提出用の完全な IGC
 * （G レコードによる署名など）ではなく、GV4D で再読み込みできる範囲の
 * 簡易な出力にとどめる。
 */
export function buildIgc(
  terminalId: string,
  fixes: FixSample[],
  options: BuildIgcOptions = {},
): string {
  const serial = (terminalId.replace(/[^A-Za-z0-9]/g, '') || 'GV4').slice(0, 3).toUpperCase().padEnd(3, '0');
  const first = fixes[0];
  const date =
    options.date ??
    (first
      ? {
          year: new Date(first.t).getUTCFullYear(),
          month: new Date(first.t).getUTCMonth() + 1,
          day: new Date(first.t).getUTCDate(),
        }
      : { year: 1970, month: 1, day: 1 });

  const lines: string[] = [
    `AXGV${serial},${terminalId}`,
    `HFDTE${pad(date.day, 2)}${pad(date.month, 2)}${pad(date.year % 100, 2)}`,
    `HFGIDGLIDERID:${terminalId}`,
    'HFFTYFRTYPE:GV4D LiveTrack',
    // VAR（昇降率）のみを拡張フィールドとして持つ（B レコード36〜39桁目）
    'I013639VAR',
  ];

  for (const f of fixes) {
    lines.push(
      `B${formatIgcTime(f.t)}${formatIgcLat(f.lat)}${formatIgcLon(f.lon)}A` +
        `${formatIgcAlt(f.altPressure ?? null)}${formatIgcAlt(f.altMsl ?? null)}` +
        formatIgcVar(f.verticalSpeed ?? null),
    );
  }

  return lines.join('\r\n') + '\r\n';
}

/**
 * 速度・方位・（欠けていれば）昇降率を前後の測位から補う。
 *
 * 中央差分を使う。片側差分より雑音に強く、気球の緩やかな運動に向く。
 * 気球の対地速度・進行方向は**そのままその高度の風**なので、
 * ここで得た値が後段の風解析の入力になる。
 */
export function deriveMotion(fixes: FixSample[]): void {
  const R_LAT = 110574;
  const R_LON = 111320;
  for (let i = 0; i < fixes.length; i++) {
    const prev = fixes[Math.max(0, i - 1)];
    const next = fixes[Math.min(fixes.length - 1, i + 1)];
    const dt = (next.t - prev.t) / 1000;
    if (dt <= 0) continue;

    const latMid = ((prev.lat + next.lat) / 2) * (Math.PI / 180);
    const dx = (next.lon - prev.lon) * R_LON * Math.cos(latMid);
    const dy = (next.lat - prev.lat) * R_LAT;

    fixes[i].speed = Math.hypot(dx, dy) / dt;
    // 静止に近いときの方位は意味を持たないので更新しない
    if (fixes[i].speed! > 0.3) {
      fixes[i].heading = ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
    }
    if (fixes[i].verticalSpeed == null && prev.altMsl !== null && next.altMsl !== null) {
      fixes[i].verticalSpeed = (next.altMsl - prev.altMsl) / dt;
    }
  }
}
